// @flow

import CouponStorage from "../helper/storage/CouponStorage";
import {createSlice} from "@reduxjs/toolkit";
import {configSet} from "./configReducer";
import {cartAndCouponSuccessChecked, cartSuccessChecked, orderDetailsCleared} from "./orderReducer";
import {allFromCartRemoved} from "./cartReducer";
import RootNodeProvider from "../helper/RootNodeProvider";
import StringUtils from "../helper/stringUtils";
import {CouponState} from "../models/Coupon";
import type {Coupon} from "../models/Coupon";

export type DecodedEmbeddedSps = {
  Name: string,
  Vorname: string,
  Strasse: string,
  PLZ: string,
  Ort: string,
  Email: string,
  DealerID: string,
  KundenID: string,
  Kundennummer: string,
}

export type SpsCustomer = {
  deposit: ?number,
  currency: ?string,
  spsDealerId: string,
  spsCustomerId: string,
  spsCustomerNo: string,
}

export type DecodedEmbeddedSly = {
    cardnumber: number,
    location: number,
    pointvalue: number,
}

export type SlyCustomer = {
    dealerNo: string,
    token: string,
}

export type CouponReducerState = {
    dealerId: string,
    coupon: Coupon,
    couponErrorCode: string,
    couponSuccessCode: string,
    slyToken: string,
    sps: ?SpsCustomer,
    sly: ?SlyCustomer,
};

let decodedEmbeddedSps: ?DecodedEmbeddedSps = null;
let decodedEmbeddedSly: ?DecodedEmbeddedSly = null;
let embeddedSly: ?string = null;

const getInitialState = () => {
    if (RootNodeProvider.getRootNode() && RootNodeProvider.getRootNode().dataset) {
        const embeddedSps = RootNodeProvider.getRootNode().dataset.embedSps ? RootNodeProvider.getRootNode().dataset.embedSps : null;
        console.log("couponReducer, embedSPS: ", embeddedSps);
        if (embeddedSps) {
            decodedEmbeddedSps = StringUtils.decodeBase64Json(embeddedSps);
        }
        embeddedSly = RootNodeProvider.getRootNode().dataset.embedSmartloyalty ? RootNodeProvider.getRootNode().dataset.embedSmartloyalty : null;
        console.log("couponReducer, embedSLY: ", embeddedSly);
        if (embeddedSly) {
            decodedEmbeddedSly = StringUtils.parseJwt(embeddedSly);
            console.log("couponReducer, decodedEmbeddedSly: ", decodedEmbeddedSly);
        }
    }

    return {
        dealerId: null,
        coupon: null,
        couponErrorCode: null,
        couponSuccessCode: null,
        slyToken: embeddedSly,
        sps: decodedEmbeddedSps
            ? {
                /* the deposit sent within the embed code must be ignored (#56170),
                   it is fetched from the SPS deposit endpoint instead */
                deposit: null,
                currency: null,
                spsDealerId: decodedEmbeddedSps.DealerID,
                spsCustomerId: decodedEmbeddedSps.KundenID,
                spsCustomerNo: decodedEmbeddedSps.Kundennummer
            }
            : null,
        sly: decodedEmbeddedSly
            ? {
                cardnumber: decodedEmbeddedSly.cardnumber,
                location: decodedEmbeddedSly.location,
                pointvalue: decodedEmbeddedSly.pointvalue,
            }
            : null,
    };
};

const couponSlice = createSlice({
  name: "coupon",
  initialState: getInitialState(),
  reducers: {
    couponSuccessChecked(state, action) {
      const couponStorage = new CouponStorage(state.dealerId);
      couponStorage.set(JSON.stringify(action.payload.coupon));

      state.coupon = action.payload.coupon;
      state.couponErrorCode = null;
      state.couponSuccessCode = CouponState[action.payload.coupon.couponState];
    },
    couponErrorChecked(state, action) {
      const couponStorage = new CouponStorage(state.dealerId);
      couponStorage.clear();

      state.coupon = null;
      state.couponErrorCode = action.payload.errorCode;
    },
    couponReset(state) {
      const couponStorage = new CouponStorage(state.dealerId);
      couponStorage.clear();

      state.coupon = null;
      state.couponErrorCode = null;
      state.couponSuccessCode = null;
    },
    couponToastReset(state) {
      state.couponErrorCode = null;
      state.couponSuccessCode = null;
    },
    spsDepositSet(state, action) {
      if (state.sps) {
        state.sps.deposit = action.payload.deposit;
        state.sps.currency = action.payload.currency;
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(configSet, (state, action) => {
        state.dealerId = action.payload.dealerId;
        state.coupon = action.payload.coupon;
      })
      .addCase(cartAndCouponSuccessChecked, (state, action) => {
        if (action.payload.coupon) {
          const couponStorage = new CouponStorage(state.dealerId);
          couponStorage.set(JSON.stringify(action.payload.coupon));
          state.coupon = action.payload.coupon;
          state.couponErrorCode = null;
        }
      })
      .addCase(allFromCartRemoved, (state, _action) => {
        const couponStorage = new CouponStorage(state.dealerId);
        couponStorage.clear();

        state.coupon = null;
        state.couponErrorCode = null;
        state.couponSuccessCode = null;
      })
      .addCase(cartSuccessChecked, (state, action) => {
        if (action.payload.coupon) {
          const couponStorage = new CouponStorage(state.dealerId);
          couponStorage.set(JSON.stringify(action.payload.coupon));

          state.coupon = action.payload.coupon;
          state.couponErrorCode = null;
        }
      })
      .addCase(orderDetailsCleared, state => {
        const couponStorage = new CouponStorage(state.dealerId);
        couponStorage.clear();

        state.coupon = null;
        state.couponErrorCode = null;
        state.couponSuccessCode = null;
      })
    ;
  }
});

export const {
  couponReset,
  couponErrorChecked,
  couponSuccessChecked,
  couponToastReset,
  spsDepositSet
} = couponSlice.actions;

export default couponSlice.reducer;
